import { ComponentPropsWithoutRef, ReactNode, Suspense } from "react";
import { Await } from "react-router-dom";
import { useCoursesExtras } from "../../homeContext.ts";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import MiscellaneousMail from "../../../../common/icons/MiscellaneousMail.tsx";
import { useIntl } from "react-intl";
import MiscellaneousExternalWindow from "../../../../common/icons/MiscellaneousExternalWindow.tsx";
import styles from "./GrayButtonList.module.scss";
import { clsx } from "clsx";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import useStoreLinks from "../../../../common/features/store/useStoreLinks.ts";
import MiscellaneousTag from "../../../../common/icons/MiscellaneousTag.tsx";
import { getAuthenticatedRedirectLink } from "../../../user/redirect/redirectHelpers.ts";
import { DeweySchema } from "../../../root/helpers/brandSchema.ts";

type BaseLinkOrButtonProps = ComponentPropsWithoutRef<typeof BaseLinkOrButton>;

const customButtonIconMap: Record<
  DeweySchema["CustomButton"]["icon"],
  ReactNode
> = {
  Tag: <MiscellaneousTag />,
  "": null,
};

export default function GrayButtonList({
  className,
  linkClassName,
  showIcons = true,
}: {
  className?: string;
  linkClassName?: string;
  showIcons?: boolean;
}) {
  const data = useCoursesExtras();
  const brand = useBrandLoaderData();
  const { isStoreEnabled, makeBasicStoreURL } = useStoreLinks();
  const intl = useIntl();

  function renderListItem({
    label,
    icon,
    href,
  }: {
    label: string;
    icon?: ReactNode;
    href: string;
  } & BaseLinkOrButtonProps) {
    return (
      <li key={label}>
        <BaseLinkOrButton href={href} className={linkClassName}>
          {showIcons && icon}
          {label}
        </BaseLinkOrButton>
      </li>
    );
  }

  function renderLinks() {
    if (brand.VirtualStore.companyId) {
      return [
        {
          icon: <MiscellaneousExternalWindow />,
          label:
            brand.VirtualStore.Merch.text ||
            intl.formatMessage({ defaultMessage: "Apparel & Gifts" }),
          // URL should always be configured if enabled, fallback for typing
          href: brand.VirtualStore.Merch.url ?? "#",
          enabled: brand.VirtualStore.Merch.enabled,
        },
        {
          icon: <MiscellaneousExternalWindow />,
          label: intl.formatMessage({ defaultMessage: "Sell Books" }),
          href: brand.VirtualStore.SellBooks.url,
          enabled: brand.VirtualStore.SellBooks.enabled,
        },
      ]
        .filter((el) => el.enabled)
        .map((link) => renderListItem(link));
    }

    if (brand.MyCourses.enabled) {
      return (
        <Suspense>
          <Await resolve={data}>
            {({ links, isInstructor, analyticsUrl }: Awaited<typeof data>) => {
              return (
                <>
                  {isInstructor &&
                    analyticsUrl &&
                    renderListItem({
                      label: intl.formatMessage({
                        defaultMessage: "Instructor Analytics",
                      }),
                      href: analyticsUrl,
                    })}
                  {links
                    .filter(({ href, label }) => href && label)
                    .map(({ href, label }) => {
                      const icon = href.startsWith("mailto:") ? (
                        <MiscellaneousMail />
                      ) : (
                        <MiscellaneousExternalWindow />
                      );
                      return renderListItem({
                        icon,
                        label,
                        href,
                      });
                    })}
                </>
              );
            }}
          </Await>
        </Suspense>
      );
    }

    return [
      {
        icon: customButtonIconMap[brand.CustomButton.icon],
        label: brand.CustomButton.label,
        href: brand.CustomButton.sso
          ? getAuthenticatedRedirectLink(brand.CustomButton.url)
          : brand.CustomButton.url,
        enabled: brand.CustomButton.enabled,
      },
      {
        icon: <MiscellaneousTag />,
        label: intl.formatMessage({
          defaultMessage: "Store",
        }),
        href: makeBasicStoreURL({ utm_medium: "bookshelf-home-left-nav" })!,
        enabled: isStoreEnabled,
      },
    ]
      .filter((el) => el.enabled)
      .map((link) => renderListItem(link));
  }

  return <ul className={clsx(className, styles.list)}>{renderLinks()}</ul>;
}
